import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/** Theme-private signal so `<cart-drawer-component>` can restore cart-actions to home before a section morph. */
const CART_DRAWER_PRE_MORPH_EVENT = 'cart-drawer:pre-morph';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 * @property {HTMLElement} [cartDiscountSuccess] - The inline success element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {number} */
  #applyPendingCount = 0;

  /** @type {Map<string, number>} */
  #removePendingCounts = new Map();

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const applyButton = form.querySelector('.cart-discount__button');
    let didStartApplyPending = false;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;

    const abortController = this.#createAbortController();

    const deferred = CartDiscountUpdateEvent.createPromise();
    let didDispatchDiscountEvent = false;
    let discountSettled = false;
    /** @param {any} value */
    const resolveDiscount = (value) => {
      if (discountSettled) return;
      discountSettled = true;
      deferred.resolve(value);
    };
    /** @param {any} error */
    const rejectDiscount = (error) => {
      if (discountSettled) return;
      discountSettled = true;
      deferred.reject(error);
    };

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (this.#hasDiscountCode(existingDiscounts, discountCodeValue)) {
        discountCode.value = '';
        this.#showInlineSuccess(discountCodeValue, true);
        return;
      }

      this.#hideInlineFeedback();

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      this.#beginApplyPending(applyButton);
      didStartApplyPending = true;

      document.dispatchEvent(
        new CartDiscountUpdateEvent({
          discountCodes: [...existingDiscounts, discountCodeValue].map((code) => ({ code })),
          promise: deferred.promise,
          detail: { action: 'apply', sectionId: this.dataset.sectionId, sourceId: this.id },
        })
      );
      didDispatchDiscountEvent = true;

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (!response.ok) {
        this.#handleRequestError(discountCodeValue);
        rejectDiscount(new Error('Discount request failed'));
        return;
      }

      const discountCodes = Array.isArray(data.discount_codes) ? data.discount_codes : [];
      const failedDiscount = discountCodes.find((/** @type {{ code?: string; applicable?: boolean }} */ discount) => {
        return (
          typeof discount.code === 'string' &&
          discount.code.toLowerCase() === discountCodeValue.toLowerCase() &&
          discount.applicable === false
        );
      });

      if (failedDiscount) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code', discountCodeValue);
        resolveDiscount({ cart: null, detail: { didError: true, action: 'apply', sourceId: this.id } });
        return;
      }

      const newHtml = data.sections?.[this.dataset.sectionId];
      if (typeof newHtml !== 'string' || newHtml.length === 0) {
        this.#handleRequestError(discountCodeValue);
        rejectDiscount(new Error('Discount response missing section'));
        return;
      }

      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountPills = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountPills)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter((code) => typeof code === 'string');
        const appliedDiscount = discountCodes.find((/** @type {{ code?: string; applicable?: boolean }} */ discount) => {
          return (
            typeof discount.code === 'string' &&
            discount.code.toLowerCase() === discountCodeValue.toLowerCase() &&
            discount.applicable === true
          );
        });

        if (
          appliedDiscount &&
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => this.#hasDiscountCode(existingDiscounts, code))
        ) {
          discountCode.value = '';
          this.#handleDiscountError('shipping', discountCodeValue);
          resolveDiscount({ cart: null, detail: { didError: true, action: 'apply', sourceId: this.id } });
          return;
        }
      }

      this.#notifyPreMorph();
      resolveDiscount({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        detail: {
          sections: data.sections,
          sectionId: this.dataset.sectionId,
          sourceId: this.id,
          resource: data,
          action: 'apply',
        },
      });
      await morphSection(this.dataset.sectionId, newHtml);

      discountCode.value = '';
      this.#revealApplyFeedback(discountCodeValue, false);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        this.#handleRequestError(discountCodeValue);
      }
      rejectDiscount(error);
    } finally {
      if (didDispatchDiscountEvent && !discountSettled) {
        rejectDiscount(new Error('Discount update did not settle'));
      }

      if (!didStartApplyPending) {
        this.#activeFetch = null;
        cartPerformance.measureFromEvent('discount-update:user-action', event);
        return;
      }

      this.#endApplyPending(applyButton);
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent || event instanceof KeyboardEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const removeButton = event.target.closest('.cart-discount__pill-remove');
    let didStartRemovePending = false;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    const deferred = CartDiscountUpdateEvent.createPromise();
    let didDispatchDiscountEvent = false;
    let discountSettled = false;
    /** @param {any} value */
    const resolveDiscount = (value) => {
      if (discountSettled) return;
      discountSettled = true;
      deferred.resolve(value);
    };
    /** @param {any} error */
    const rejectDiscount = (error) => {
      if (discountSettled) return;
      discountSettled = true;
      deferred.reject(error);
    };

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      this.#beginRemovePending(discountCode, pill, removeButton);
      didStartRemovePending = true;

      document.dispatchEvent(
        new CartDiscountUpdateEvent({
          discountCodes: existingDiscounts.map((code) => ({ code })),
          promise: deferred.promise,
          detail: { action: 'remove', sectionId: this.dataset.sectionId, sourceId: this.id },
        })
      );
      didDispatchDiscountEvent = true;

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (!response.ok) {
        this.#handleRequestError(discountCode);
        rejectDiscount(new Error('Discount request failed'));
        return;
      }

      const sectionHtml = data.sections?.[this.dataset.sectionId];
      if (typeof sectionHtml !== 'string' || sectionHtml.length === 0) {
        this.#handleRequestError(discountCode);
        rejectDiscount(new Error('Discount response missing section'));
        return;
      }

      this.#notifyPreMorph();
      resolveDiscount({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        detail: {
          sections: data.sections,
          sectionId: this.dataset.sectionId,
          sourceId: this.id,
          resource: data,
          action: 'remove',
        },
      });
      await morphSection(this.dataset.sectionId, sectionHtml);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        this.#handleRequestError(discountCode);
      }
      rejectDiscount(error);
    } finally {
      if (didDispatchDiscountEvent && !discountSettled) {
        rejectDiscount(new Error('Discount update did not settle'));
      }

      if (!didStartRemovePending) {
        this.#activeFetch = null;
        return;
      }

      this.#endRemovePending(discountCode, pill, removeButton);
      this.#activeFetch = null;
    }
  };

  /**
   * Signals the cart drawer to restore cart-actions to its home position before a section morph
   * replaces the DOM the drawer relocated it into.
   */
  #notifyPreMorph() {
    this.dispatchEvent(new CustomEvent(CART_DRAWER_PRE_MORPH_EVENT, { bubbles: true }));
  }

  /**
   * @param {'discount_code' | 'shipping'} type
   * @param {string} discountCodeValue
   */
  #handleDiscountError(type, discountCodeValue) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping, cartDiscountSuccess } =
      this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;

    cartDiscountSuccess?.classList.add('hidden');
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');

    if (target instanceof HTMLElement) {
      target.textContent =
        type === 'shipping'
          ? this.#messageForShippingError(discountCodeValue)
          : this.#messageForDiscountError(discountCodeValue);
    }

    document.dispatchEvent(
      new CustomEvent('discount:error', {
        detail: {
          type,
          code: discountCodeValue,
        },
      })
    );
  }

  /**
   * @param {string} discountCodeValue
   */
  #handleRequestError(discountCodeValue) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping, cartDiscountSuccess } =
      this.refs;

    cartDiscountSuccess?.classList.add('hidden');
    cartDiscountErrorShipping?.classList.add('hidden');
    cartDiscountError?.classList.remove('hidden');
    cartDiscountErrorDiscountCode?.classList.remove('hidden');

    if (cartDiscountErrorDiscountCode instanceof HTMLElement) {
      cartDiscountErrorDiscountCode.textContent = this.#decodeHtmlEntities(Theme.translations.discount_request_error) || null;
    }

    document.dispatchEvent(
      new CustomEvent('discount:error', {
        detail: {
          type: 'discount_code',
          code: discountCodeValue,
        },
      })
    );

    this.dispatchEvent(
      new CartErrorEvent({
        error: this.#decodeHtmlEntities(Theme.translations.discount_request_error) || 'Discount request failed',
        code: 'SERVICE_UNAVAILABLE',
        detail: { source: 'cart-discount', discountCode: discountCodeValue },
      })
    );
  }

  /**
   * @param {string} code
   * @param {boolean} [alreadyApplied]
   */
  #showInlineSuccess(code, alreadyApplied = false) {
    const { cartDiscountSuccess, cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } =
      this.refs;

    cartDiscountError?.classList.add('hidden');
    cartDiscountErrorDiscountCode?.classList.add('hidden');
    cartDiscountErrorShipping?.classList.add('hidden');

    if (cartDiscountSuccess instanceof HTMLElement) {
      cartDiscountSuccess.textContent = this.#messageForSuccess(code, alreadyApplied);
      cartDiscountSuccess.classList.remove('hidden');
    }

    document.dispatchEvent(
      new CustomEvent('discount:notification', {
        detail: {
          code,
          alreadyApplied,
        },
      })
    );
  }

  /**
   * @param {string} code
   * @param {boolean} [alreadyApplied]
   */
  showApplyFeedback(code, alreadyApplied = false) {
    this.#showInlineSuccess(code, alreadyApplied);
  }

  /**
   * @param {string} code
   * @param {boolean} [alreadyApplied]
   */
  #revealApplyFeedback(code, alreadyApplied = false) {
    const component = this.#getConnectedComponent();
    if (component) {
      component.showApplyFeedback(code, alreadyApplied);
      return;
    }

    this.#showInlineSuccess(code, alreadyApplied);
  }

  /**
   * @returns {CartDiscount | null}
   */
  #getConnectedComponent() {
    if (typeof this.dataset.sectionId !== 'string') return null;

    const section = document.getElementById(`shopify-section-${this.dataset.sectionId}`);
    const component = section?.querySelector('cart-discount-component');

    return component instanceof CartDiscount ? component : null;
  }

  #hideInlineFeedback() {
    const { cartDiscountSuccess, cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } =
      this.refs;

    cartDiscountSuccess?.classList.add('hidden');
    cartDiscountError?.classList.add('hidden');
    cartDiscountErrorDiscountCode?.classList.add('hidden');
    cartDiscountErrorShipping?.classList.add('hidden');
  }

  /**
   * @param {string} code
   * @param {boolean} alreadyApplied
   */
  #messageForSuccess(code, alreadyApplied) {
    const template = alreadyApplied
      ? Theme.translations.discount_code_already_applied
      : Theme.translations.discount_code_success;

    return this.#formatTranslation(template, code);
  }

  /**
   * @param {string} code
   */
  #messageForDiscountError(code) {
    return this.#formatTranslation(Theme.translations.discount_code_error, code);
  }

  /**
   * @param {string} code
   */
  #messageForShippingError(code) {
    return this.#decodeHtmlEntities(Theme.translations.shipping_discount_error) || this.#formatTranslation(Theme.translations.discount_code_error, code);
  }

  /**
   * @param {string | undefined} template
   * @param {string} code
   */
  #formatTranslation(template, code) {
    if (typeof template === 'string' && template.length > 0) {
      return this.#decodeHtmlEntities(template).split('__CODE__').join(code);
    }

    return code;
  }

  /**
   * @param {string | undefined} value
   * @returns {string}
   */
  #decodeHtmlEntities(value) {
    if (typeof value !== 'string' || value.length === 0) return '';

    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }

  /**
   * @param {string[]} discounts
   * @param {string} code
   */
  #hasDiscountCode(discounts, code) {
    const normalizedCode = code.toLowerCase();
    return discounts.some((discount) => discount.toLowerCase() === normalizedCode);
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }

  /**
   * Marks the apply button as pending while discount application is in-flight.
   * @param {Element | null} applyButton
   */
  #beginApplyPending(applyButton) {
    this.#applyPendingCount++;
    if (!(applyButton instanceof HTMLButtonElement)) return;
    applyButton.dataset.pending = 'true';
    applyButton.setAttribute('aria-busy', 'true');
    applyButton.disabled = true;
  }

  /**
   * Clears pending state from the apply button when all apply requests are done.
   * @param {Element | null} applyButton
   */
  #endApplyPending(applyButton) {
    this.#applyPendingCount = Math.max(0, this.#applyPendingCount - 1);
    if (this.#applyPendingCount > 0 || !(applyButton instanceof HTMLButtonElement)) return;
    delete applyButton.dataset.pending;
    applyButton.removeAttribute('aria-busy');
    applyButton.disabled = false;
  }

  /**
   * Marks a discount pill removal action as pending.
   * @param {string} discountCode
   * @param {HTMLLIElement} pill
   * @param {Element | null} removeButton
   */
  #beginRemovePending(discountCode, pill, removeButton) {
    const currentCount = this.#removePendingCounts.get(discountCode) || 0;
    this.#removePendingCounts.set(discountCode, currentCount + 1);

    pill.dataset.pending = 'true';
    pill.setAttribute('aria-busy', 'true');

    if (!(removeButton instanceof HTMLButtonElement)) return;
    removeButton.dataset.pending = 'true';
    removeButton.setAttribute('aria-busy', 'true');
    removeButton.disabled = true;
  }

  /**
   * Clears pending state for a discount pill removal action.
   * @param {string} discountCode
   * @param {HTMLLIElement} pill
   * @param {Element | null} removeButton
   */
  #endRemovePending(discountCode, pill, removeButton) {
    const nextCount = Math.max(0, (this.#removePendingCounts.get(discountCode) || 0) - 1);
    if (nextCount === 0) {
      this.#removePendingCounts.delete(discountCode);
    } else {
      this.#removePendingCounts.set(discountCode, nextCount);
    }

    if (nextCount === 0 && pill.isConnected) {
      delete pill.dataset.pending;
      pill.removeAttribute('aria-busy');
    }

    if (nextCount > 0 || !(removeButton instanceof HTMLButtonElement) || !removeButton.isConnected) return;
    delete removeButton.dataset.pending;
    removeButton.removeAttribute('aria-busy');
    removeButton.disabled = false;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
